import { logTrackerIssue, TrackerIssueKind } from '@/src/lib/or';
import type { OAuthTypes } from '@/src/modules/auth/api/auth.api';
import { useRunOnce } from '@fabric-app/hooks/useRunOnce';
import { captureException } from '@sentry/nextjs';
import { useRouter } from 'next/router';

const trackOauthRequest = (provider: OAuthTypes, url: string) => {
  sessionStorage.setItem('oauth-request', JSON.stringify({ provider, url }));
};

const removeOauthTrackedRequest = () => {
  sessionStorage.removeItem('oauth-request');
};

/**
 * This hook is used to track oauth requests that did not return to the /oauth page
 * Which is a potential issue/failure that causes the user to not be able to authenticate to the app using said provider
 * More discussed here:
 * https://discord.com/channels/934045073002364981/1328751013506584649
 */
const useTrackOauthRequestFailure = () => {
  const router = useRouter();

  useRunOnce(
    () => {
      if (router.pathname === '/oauth') {
        removeOauthTrackedRequest();
        return;
      }

      const oauthProvider = sessionStorage.getItem('oauth-provider');
      if (!oauthProvider) return;

      try {
        const { provider, url } = JSON.parse(oauthProvider);

        logTrackerIssue({
          kind: TrackerIssueKind.OAUTH_NO_CALLBACK,
          payload: {
            provider,
            url,
          },
        });

        captureException(new Error('Oauth request did not return to a /oauth page'), {
          extra: {
            provider,
            url,
          },
        });

        removeOauthTrackedRequest();
      } catch (e) {
        captureException(e);
      }
    },
    {
      condition: router.isReady,
    },
  );
};

export {
  removeOauthTrackedRequest,
  trackOauthRequest,
  useTrackOauthRequestFailure as useOauthFailTracker,
};
